import getNestedVal from 'lodash/get'; // eslint-disable-line no-restricted-imports
import { AggregateRating, ServerFeature } from 'models/app';
import { Country } from './i18n/country';
import { App } from '../models/app';
import { EntityState } from '@reduxjs/toolkit';
import { sanitize } from 'dompurify';

export function objectEnum<K extends PropertyKey>(...keys: K[]): Record<K, K> {
  return keys.reduce((objectEnumerator: Record<K, K>, key, index) => {
    if (process.env.NODE_ENV === 'development') {
      const isDuplicateKey = objectEnumerator[key];

      console.assert(!isDuplicateKey, `duplicate key '${String(key)}' at index ${index}`);
    }

    objectEnumerator[key] = key;

    return objectEnumerator;
  }, {} as Record<K, K>);
}

export function getStarRating(aggregateRating: AggregateRating): number {
  const { reviewCount, totalStars, weightedRating } = aggregateRating;
  if (weightedRating) {
    return weightedRating;
  }
  // this returns a number with 1 decimal place, using toFixed returns a string
  else return Math.round((totalStars / reviewCount) * 10) / 10 || 0;
}
export const getCountryCurrency = (countryCode: CountryCode): CurrencyCode =>
  Country[countryCode]?.currencyCode ?? countryCode;

export const COMPARE_FN = {
  number: (aVal: number, bVal: number): number => aVal - bVal,
  string: (aVal: string, bVal: string): number => aVal.localeCompare(bVal),
};

/**
 * Compares the element before and after and returns the order number if each item
 * When objects are undefined, getNestedVal will return undefined
 * @param objA - current item
 * @param objB - next item
 * @param path - path to nested value
 * @returns {(a, b, key) => number)}
 */
export function getCompareFn<T>(objA: T, objB: T, path: string): number {
  const valA = getNestedVal(objA, path);
  const valB = getNestedVal(objB, path);

  if (!valA && !valB) return 0;
  if (!valA && valA !== 0) return 1;
  if (!valB && valB !== 0) return -1;
  if (typeof valA === 'string') {
    return COMPARE_FN.string(valA, valB);
  } else if (typeof valA === 'number') {
    return COMPARE_FN.number(valA, valB);
  } else {
    return 0;
  }
}

// Matches a valid Brightcove URL. Supported formats are:
// - players.brightcove.net/12345678/default-default/index.html?videoId=12345678
export const URL_BRIGHTCOVE = /players\.brightcove\.net\/\d+\/default_default\/index\.html\?videoId=\d+/;

// Matches any valid Vimeo URL and captures the ID. Supported formats are:
// - vimeo.com/12345678
// - player.vimeo.com/video/12345678
export const URL_VIMEO = /(?:vimeo\.com|player\.vimeo\.com\/video)\/(\d+)/;

// Matches any valid YouTube URL and captures the ID. Supported formats are:
// - youtube.com/watch?v=F68TYGsc9N4
// - youtube.com/watch?foo=bar&v=F68TYGsc9N4
// - youtube.com/embed/F68TYGsc9N4
// - youtu.be/F68TYGsc9N4
export const URL_YOUTUBE = /youtu(?:\.be\/|be\.com\/(?:watch\?(?:v|.*&v)=|embed\/))([\w-]{11})(?![\w-])/;

const tryNormalizeBrightcove = (url: string) => {
  const match = URL_BRIGHTCOVE.exec(url);

  return match ? `//players.brightcove.net/${match[1]}/default_default/index.html?videoId=${match[2]}` : '';
};

const tryNormalizeVimeoUrl = (url: string) => {
  const match = URL_VIMEO.exec(url);

  return match ? `//player.vimeo.com/video/${match[1]}?playlist=${match[1]}&loop=1` : '';
};

const tryNormalizeYouTubeUrl = (url: string) => {
  const match = URL_YOUTUBE.exec(url);

  return match ? `//www.youtube.com/embed/${match[1]}?playlist=${match[1]}&loop=1&rel=0` : '';
};

export const normalizeUrl = (videoUrl?: string): string => {
  if (!videoUrl) return '';

  return (
    tryNormalizeYouTubeUrl(videoUrl) || tryNormalizeVimeoUrl(videoUrl) || tryNormalizeBrightcove(videoUrl)
  );
};

export const shuffleArray = (n: App[]): App[] => {
  let currentIndex = n.length;
  let otherIndex: number;
  while (currentIndex != 0) {
    otherIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    /*Shuffling the indexes*/
    [n[currentIndex], n[otherIndex]] = [n[otherIndex], n[currentIndex]];
  }

  return n;
};

export const convertPixelStringToInteger = (pixelStr: string): number => {
  return parseInt(pixelStr) || 0;
};

export const truncateString = (str: string | undefined, length: number, noEllipsis?: boolean): string => {
  if (!str) return '';
  if (noEllipsis) {
    return str.length > length ? str.substr(0, length) : str;
  } else return str.length > length ? str.substr(0, length) + '...' : str;
};

export const asciiToHex = (str: string): string => {
  let hexStr = '';

  for (let n = 0; n < str.length; n++) {
    let hex = Number(str.charCodeAt(n)).toString(16);
    hexStr += hex;
  }
  return hexStr;
};

export const hexToAscii = (hexStr: string): string => {
  let str = '';
  for (let n = 0; n < hexStr.length; n += 2) {
    str += String.fromCharCode(parseInt(hexStr.substr(n, 2), 16));
  }
  return str;
};

export const typeMatch = (obj: any, type: string): boolean => {
  const deducedType = Object.prototype.toString.call(obj).toLowerCase();
  return deducedType.indexOf(type) > -1;
};

export const removeMatchingElementFromArray = <T>(arr: T[], element: T): T[] => {
  let index = arr.indexOf(element);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
};

export const getListFromEntityState = <T>(data: Maybe<EntityState<T>>): Maybe<T[]> => {
  if (!data) return;

  return Object.values(data.entities) as T[];
};

export const decodeHTMLEntities = (str: string): string => {
  let result;
  let element = document.createElement('div');

  if (str) {
    // strip script/html tags
    result = str
      .replace(/<script[^>]*>([\S\s]*?)<\/script>/gim, '')
      .replace(/<\/?\w(?:[^"'>]|"[^"]*"|'[^']*')*>/gim, '');
    element.innerHTML = result;
    result = element.textContent;
    element.textContent = '';
  }

  return result || '';
};

export const sanitizeText = (text: string) => {
  return sanitize(text, { FORBID_TAGS: ['style'], FORBID_ATTR: ['style'] }).trim();
};

export const mapBy = <T>(collection: T[], key: keyof T): Map<T[keyof T], T> => {
  let map = new Map<T[keyof T], T>();
  collection.forEach(collectionElement => {
    const keyVal = collectionElement[key];
    map.set(keyVal, collectionElement);
  });
  return map;
};

//This method determines if collectionA is a subset of collectionB
export const collectionIsASubset = <T = any>(collectionA: T[], collectionB: T[], key: keyof T): boolean => {
  const collectionBMap = mapBy<T>(collectionB, key);
  return collectionA.every(collectionAElement => {
    const identifier = collectionAElement[key];
    return Boolean(collectionBMap.get(identifier));
  });
};

export const isFeatureFlagEnabled = (featureData: Maybe<EntityState<ServerFeature>>): boolean => {
  const id = featureData && featureData.ids && featureData.ids.length ? featureData.ids[0] : '';
  let feature: Maybe<ServerFeature>;
  if (id) {
    feature = featureData!.entities[id];
  }
  return !!feature && feature.enabled;
};
